import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  Card,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  useTheme
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { strings } from "../../../localization/LocalizedStrings";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { TextField } from "../../TextField";
import { AlertDialog } from "../../AlertDialog";
import { SwitchListItem } from "../../SwitchListItem";
import { toast } from "react-toastify";
import { FieldTitle } from "../../Text/FieldTitle";
import { ContainedButton } from "../../ContainedButton";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { FreeTextListItem } from "../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../attributes/ModifyFreeTextAttributeDialog";
import { IShelving, ShelvingOrientation, ShelvingTheme } from "../../../hooks/shelvings";
import { useAddShelving } from "../../../hooks/shelvings/useAddShelving";
import { useModifyShelving } from "../../../hooks/shelvings/useModifyShelving";
import { Firebase } from "../../../services/Firebase";
import { ProductPurchasesDialog } from "../../products/ProductPurchasesDialog";
import { ProductType } from "../../../hooks/products/interfaces";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";
import { ShelvingThemeChip } from "./ShelvingThemeChip";

export const ModifyShelvingDialog: FunctionComponent<IModifyShelvingDialogProps> = ({
  open,
  setOpen,
  shelving,
  callback,
  onDelete
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [dialogTitle, setDialogTitle] = useState(strings.shelving_new_title);
  const { addShelving } = useAddShelving();
  const { modifyShelving } = useModifyShelving();
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [purchaseAlertIsOpen, setPurchaseAlertIsOpen] = useState(false);
  const [purchaseDialogIsOpen, setPurchaseDialogIsOpen] = useState(false);
  const tryToClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
      return;
    }
    handleClose();
  };
  const handleClose = () => {
    setOpen(false);
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: shelving?.name ?? "",
      description: shelving?.description ?? "",
      orientation: shelving?.orientation ?? "horizontal",
      theme: shelving?.theme ?? "cedar",
      everyAlbumHasSameSize: shelving?.everyAlbumHasSameSize ?? false
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, description, orientation, theme, everyAlbumHasSameSize }) => {
      setIsLoading(true);
      let statusCode, responseBody;
      if (shelving) {
        const { status, body } = await modifyShelving({
          uuid: shelving.uuid,
          name: name,
          description: description,
          orientation: orientation,
          theme: theme,
          everyAlbumHasSameSize: everyAlbumHasSameSize
        });
        statusCode = status;
        responseBody = body;
      } else {
        const { status, body } = await addShelving({
          name: name,
          description: description,
          orientation: orientation,
          theme: theme,
          everyAlbumHasSameSize: everyAlbumHasSameSize
        });
        statusCode = status;
        responseBody = body;
      }
      if (StatusCodesHelper.isSuccessful(statusCode)) {
        Firebase.shared.logFirebaseEvent("shelving_action");
        if (callback != undefined) {
          callback(responseBody);
        }
        handleClose();
        toast.success(strings.generic_update_success);
      } else if (StatusCodesHelper.isLimitReached(statusCode)) {
        setAlertMessage(responseBody.message);
        setPurchaseAlertIsOpen(true);
      } else {
        setAlertMessage(responseBody.message);
        setAlertIsOpen(true);
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  useEffect(() => {
    if (!open) return;
    setDialogTitle(shelving ? strings.shelving_title : strings.shelving_new_title);
    formik.resetForm();
  }, [open]);
  const handleOrientationChange = (
    event: React.MouseEvent<HTMLElement>,
    orientation: ShelvingOrientation
  ) => {
    if (orientation !== null) {
      formik.setFieldValue("orientation", orientation);
    }
  };
  const possibleThemes: ShelvingTheme[] = ["cedar", "oak", "ebony", "walnut", "blue"];
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {shelving && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.shelving_name_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.shelving_orientation}</FieldTitle>
          <SwitchListItem
            className={styles.orientation}
            labels={[strings.horizontal, strings.vertical]}
            values={["horizontal", "vertical"]}
            value={formik.values.orientation}
            fullWidth={true}
            disableGutters
            disabled={isLoading}
            handleChange={handleOrientationChange}
          />
          <FieldTitle>{strings.shelving_theme}</FieldTitle>
          <Select
            value={String(formik.values.theme)}
            renderValue={() => <ShelvingThemeChip theme={formik.values.theme} />}
            className={styles.selectTheme}
            onChange={(event: SelectChangeEvent) =>
              formik.setFieldValue("theme", event.target.value)
            }
            sx={{ height: "36px" }}
          >
            {possibleThemes.map((theme, index) => (
              <MenuItem key={`item_${index}`} value={theme}>
                <ShelvingThemeChip theme={theme} />
              </MenuItem>
            ))}
          </Select>
          <FieldTitle>{strings.shelving_every_album_same_size}</FieldTitle>
          <ToggleButtonGroup
            className={styles.switch}
            value={formik.values.everyAlbumHasSameSize}
            exclusive
            onChange={(event, value) => {
              if (value !== null) {
                formik.setFieldValue("everyAlbumHasSameSize", value);
              }
            }}
            sx={{ height: "30px", width: "100%" }}
          >
            <ToggleButton
              className={styles.toggleButton}
              sx={{ color: theme.palette.colors?.disabled }}
              value={true}
            >
              {strings.stats_pie_yes}
            </ToggleButton>
            <ToggleButton
              className={styles.toggleButton}
              sx={{ color: theme.palette.colors?.disabled }}
              value={false}
            >
              {strings.stats_pie_no}
            </ToggleButton>
          </ToggleButtonGroup>
          <FieldTitle>{strings.collection_description}</FieldTitle>
          <Card className={styles.descriptionCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={handleClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={purchaseAlertIsOpen}
        setOpen={setPurchaseAlertIsOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setPurchaseDialogIsOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.SHELVINGS}
        open={purchaseDialogIsOpen}
        setOpen={setPurchaseDialogIsOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyShelvingDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  shelving?: IShelving;
  callback?: (newShelving: IShelving) => void;
  onDelete?: () => void;
}
